import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AlertTriangle, ImagePlus, Loader2, Plus, Trash2, X } from "lucide-react";
import { api, apiError, fileUrl, inr2, uploadFile } from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { Field } from "@/pages/Leads";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const nextUid = () => `line-${Math.random().toString(36).slice(2, 10)}`;

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

export default function QuotationBuilder() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(Boolean(id));
  const [existing, setExisting] = useState(null);
  const [priceHistory, setPriceHistory] = useState([]);
  const fileRef = useRef(null);
  const [uploadTarget, setUploadTarget] = useState(null);

  const [form, setForm] = useState({
    customer_id: "", project_name: "", site_address: "", valid_until: "",
    template: "premium", photo_layout: "above", gst_mode: "auto",
    installation_charge: 0, transportation_charge: 0, other_charges: 0, charges_gst_rate: 18,
    payment_terms: "50% advance with order, 40% before dispatch, 10% after installation",
    warranty: "12 months against manufacturing defects", terms: "", notes: "",
    items: [],
  });

  useEffect(() => {
    api.get("/customers?limit=200").then(({ data }) => setCustomers(data.items));
    api.get("/products?limit=200").then(({ data }) => setProducts(data.items));
    api.get("/settings").then(({ data }) => {
      setSettings(data);
      if (id) return;
      setForm((f) => ({
        ...f,
        terms: data.quotation?.default_terms || "",
        template: data.quotation?.default_template || "premium",
        photo_layout: data.quotation?.photo_layout || "above",
        valid_until: new Date(Date.now() + (data.quotation?.validity_days || 15) * 86400000)
          .toISOString()
          .slice(0, 10),
      }));
    });
  }, [id]);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    api
      .get(`/quotations/${id}`)
      .then(({ data }) => {
        setExisting(data);
        setForm({
          customer_id: data.customer_id || "",
          project_name: data.project_name || "",
          site_address: data.site_address || "",
          valid_until: (data.valid_until || "").slice(0, 10),
          template: data.template || "premium",
          photo_layout: data.photo_layout || "above",
          gst_mode: data.gst_mode || "auto",
          installation_charge: data.installation_charge || 0,
          transportation_charge: data.transportation_charge || 0,
          other_charges: data.other_charges || 0,
          charges_gst_rate: data.charges_gst_rate ?? 18,
          payment_terms: data.payment_terms || "",
          warranty: data.warranty || "",
          terms: data.terms || "",
          notes: data.notes || "",
          items: (data.items || []).map((it) => ({ ...it, uid: nextUid() })),
        });
      })
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  }, [id]);

  const totals = useMemo(() => {
    let subtotal = 0, discount = 0, tax = 0, cost = 0;
    form.items.forEach((it) => {
      const gross = round2(Number(it.quantity) * Number(it.rate));
      const disc = round2((gross * Number(it.discount_percent || 0)) / 100);
      const taxable = round2(gross - disc);
      subtotal += gross;
      discount += disc;
      tax += round2((taxable * Number(it.gst_rate || 0)) / 100);
      cost += round2(Number(it.cost || 0) * Number(it.quantity));
    });
    const charges = round2(
      Number(form.installation_charge || 0) +
        Number(form.transportation_charge || 0) +
        Number(form.other_charges || 0)
    );
    const chargesTax = round2((charges * Number(form.charges_gst_rate || 0)) / 100);
    const taxable = round2(subtotal - discount + charges);
    const taxTotal = round2(tax + chargesTax);
    const grand = round2(taxable + taxTotal);
    const margin = round2(taxable - cost);
    return {
      subtotal: round2(subtotal), discount: round2(discount), charges, taxable,
      tax: taxTotal, grand, cost: round2(cost), margin,
      marginPct: taxable ? round2((margin / taxable) * 100) : 0,
    };
  }, [form]);

  const effectiveMode = useMemo(() => {
    if (form.gst_mode !== "auto") return form.gst_mode;
    const c = customers.find((x) => x.id === form.customer_id);
    const our = settings?.company?.state || "";
    if (!c?.state || !our) return "intra";
    return c.state.toLowerCase() === our.toLowerCase() ? "intra" : "inter";
  }, [form.gst_mode, form.customer_id, customers, settings]);

  const gstHint = useMemo(() => {
    const c = customers.find((x) => x.id === form.customer_id);
    if (!c) return "";
    const label = effectiveMode === "inter" ? "IGST" : "CGST + SGST";
    return `${c.state || "State not set"} → ${label}`;
  }, [customers, form.customer_id, effectiveMode]);

  const minMargin = Number(settings?.quotation?.min_margin_percent ?? 15);
  const marginWarning = totals.cost > 0 && totals.marginPct < minMargin;

  const addProduct = (productId) => {
    const p = products.find((x) => x.id === productId);
    if (!p) return;
    setForm((f) => ({
      ...f,
      items: [
        ...f.items,
        {
          uid: nextUid(),
          product_id: p.id, name: p.name, description: p.description || "",
          photos: p.photos || [], specifications: p.specifications || [],
          dimensions: p.dimensions || "", quantity: 1, unit: p.unit || "Nos",
          rate: p.standard_price || 0, discount_percent: 0, gst_rate: p.gst_rate || 18,
          hsn_sac: p.hsn_sac || "", cost: p.manufacturing_cost || 0,
        },
      ],
      installation_charge: round2(Number(f.installation_charge) + Number(p.installation_charge || 0)),
      transportation_charge: round2(
        Number(f.transportation_charge) + Number(p.transportation_charge || 0)
      ),
    }));
  };

  const addBlank = () =>
    setForm((f) => ({
      ...f,
      items: [
        ...f.items,
        { uid: nextUid(), product_id: "", name: "", description: "", photos: [], specifications: [],
          dimensions: "", quantity: 1, unit: "Nos", rate: 0, discount_percent: 0,
          gst_rate: 18, hsn_sac: "", cost: 0 },
      ],
    }));

  const setItem = (idx, patch) =>
    setForm((f) => ({
      ...f,
      items: f.items.map((it, i) => (i === idx ? { ...it, ...patch } : it)),
    }));

  const loadHistory = async (idx) => {
    const it = form.items[idx];
    if (!it?.product_id || !form.customer_id) return;
    try {
      const { data } = await api.get(
        `/price-history?product_id=${it.product_id}&customer_id=${form.customer_id}`
      );
      setPriceHistory(data);
      if (!data.length) toast.info("No previous price for this customer and product");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const onUpload = async (files) => {
    if (uploadTarget === null) return;
    try {
      const urls = [];
      for (const f of files) urls.push((await uploadFile(f, "quotation")).url);
      setItem(uploadTarget, { photos: [...(form.items[uploadTarget].photos || []), ...urls] });
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const save = async (submit) => {
    if (!form.customer_id) return toast.error("Please select a customer");
    if (!form.items.length) return toast.error("Add at least one product to the quotation");
    setSaving(true);
    try {
      const payload = {
        ...form,
        installation_charge: Number(form.installation_charge || 0),
        transportation_charge: Number(form.transportation_charge || 0),
        other_charges: Number(form.other_charges || 0),
        charges_gst_rate: Number(form.charges_gst_rate || 0),
        items: form.items.map(({ uid, ...it }) => ({
          ...it,
          quantity: Number(it.quantity || 0),
          rate: Number(it.rate || 0),
          discount_percent: Number(it.discount_percent || 0),
          gst_rate: Number(it.gst_rate || 0),
          cost: Number(it.cost || 0),
        })),
      };
      if (isEdit) {
        const { data } = await api.put(`/quotations/${id}`, payload);
        toast.success(`Quotation ${data.number} updated`);
        navigate(`/quotations/${id}`);
        return;
      }
      const { data } = await api.post("/quotations", payload);
      if (submit) await api.post(`/quotations/${data.id}/submit`);
      toast.success(`Quotation ${data.number} created`);
      navigate(`/quotations/${data.id}`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div data-testid="quotation-builder">
      <PageHeader
        title={isEdit ? `Edit ${existing?.number || "quotation"}` : "New Quotation"}
        subtitle={
          isEdit
            ? "Change items, quantity, rate, discount and photographs. Saving refreshes the PDF."
            : "Select products, attach photographs, set pricing and generate a branded proposal."
        }
        actions={
          isEdit ? (
            <>
              <Button
                variant="outline"
                data-testid="quotation-cancel-edit-button"
                onClick={() => navigate(`/quotations/${id}`)}
              >
                Cancel
              </Button>
              <Button data-testid="quotation-update-button" disabled={saving} onClick={() => save(false)}>
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save changes
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outline"
                data-testid="quotation-save-draft-button"
                disabled={saving}
                onClick={() => save(false)}
              >
                Save draft
              </Button>
              <Button data-testid="quotation-submit-button" disabled={saving} onClick={() => save(true)}>
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save & send for approval
              </Button>
            </>
          )
        }
      />

      <div className="grid gap-4 p-4 sm:p-6 lg:grid-cols-[1fr_320px]">
        <div className="space-y-4">
          <div className="surface p-5">
            <div className="overline mb-3">Customer & project</div>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Customer">
                <Select
                  value={form.customer_id}
                  onValueChange={(v) => {
                    const c = customers.find((x) => x.id === v);
                    setForm({
                      ...form,
                      customer_id: v,
                      site_address: c?.shipping_address || "",
                      gst_mode: "auto",
                    });
                  }}
                >
                  <SelectTrigger data-testid="quotation-customer-select">
                    <SelectValue placeholder="Select customer" />
                  </SelectTrigger>
                  <SelectContent>
                    {customers.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.company}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Project name">
                <Input
                  data-testid="quotation-project-input"
                  value={form.project_name}
                  onChange={(e) => setForm({ ...form, project_name: e.target.value })}
                />
              </Field>
              <Field label="Valid until">
                <Input
                  type="date"
                  data-testid="quotation-validity-input"
                  value={form.valid_until}
                  onChange={(e) => setForm({ ...form, valid_until: e.target.value })}
                />
              </Field>
              <Field label="GST type">
                <Select value={form.gst_mode} onValueChange={(v) => setForm({ ...form, gst_mode: v })}>
                  <SelectTrigger data-testid="quotation-gst-mode-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">Automatic (from customer state)</SelectItem>
                    <SelectItem value="intra">Within state (CGST + SGST)</SelectItem>
                    <SelectItem value="inter">Other state (IGST)</SelectItem>
                  </SelectContent>
                </Select>
                {gstHint && (
                  <div className="mt-1 text-[11px] text-muted-foreground" data-testid="quotation-gst-hint">
                    {gstHint}
                  </div>
                )}
              </Field>
              <Field label="Site address" className="sm:col-span-2">
                <Textarea
                  value={form.site_address}
                  onChange={(e) => setForm({ ...form, site_address: e.target.value })}
                />
              </Field>
            </div>
          </div>

          <div className="surface p-5">
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <div className="overline flex-1">Products & photographs</div>
              <Select onValueChange={addProduct}>
                <SelectTrigger data-testid="quotation-add-product-select" className="w-56">
                  <SelectValue placeholder="Add from catalogue" />
                </SelectTrigger>
                <SelectContent>
                  {products.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" data-testid="quotation-add-custom-item" onClick={addBlank}>
                <Plus className="mr-1 h-3.5 w-3.5" /> Custom line
              </Button>
            </div>

            {form.items.length === 0 && (
              <div className="border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
                Add products from the catalogue — their photographs and specifications flow into the
                proposal automatically.
              </div>
            )}

            <div className="space-y-4">
              {form.items.map((it, idx) => (
                <div key={it.uid} className="rounded-md border border-border p-4" data-testid={`quotation-item-${idx}`}>
                  <div className="mb-3 flex items-start gap-2">
                    <Input
                      data-testid={`quotation-item-name-${idx}`}
                      className="font-medium"
                      placeholder="Product name"
                      value={it.name}
                      onChange={(e) => setItem(idx, { name: e.target.value })}
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      data-testid={`quotation-item-remove-${idx}`}
                      onClick={() =>
                        setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }))
                      }
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                  <Textarea
                    className="mb-3"
                    placeholder="Description shown in the proposal"
                    value={it.description}
                    onChange={(e) => setItem(idx, { description: e.target.value })}
                  />
                  <div className="mb-3 flex flex-wrap gap-2">
                    {(it.photos || []).map((p) => (
                      <div key={p} className="product-image-frame relative h-16 w-20 border border-border">
                        <img src={fileUrl(p)} alt="" />
                        <button
                          type="button"
                          onClick={() => setItem(idx, { photos: it.photos.filter((x) => x !== p) })}
                          className="absolute right-0.5 top-0.5 rounded bg-background/90 p-0.5"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      data-testid={`quotation-item-photo-${idx}`}
                      onClick={() => {
                        setUploadTarget(idx);
                        setTimeout(() => fileRef.current?.click(), 0);
                      }}
                      className="flex h-16 w-20 flex-col items-center justify-center gap-1 rounded border border-dashed border-border text-[10px] text-muted-foreground hover:bg-secondary"
                    >
                      <ImagePlus className="h-4 w-4" /> Photo
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-6">
                    {[
                      ["quantity", "Qty", `quotation-item-qty-${idx}`],
                      ["rate", "Rate ₹", `quotation-item-rate-${idx}`],
                      ["discount_percent", "Disc %", null],
                      ["gst_rate", "GST %", null],
                      ["cost", "Cost ₹", null],
                      ["hsn_sac", "HSN", null],
                    ].map(([k, label, tid]) => (
                      <Field key={k} label={label}>
                        <Input
                          data-testid={tid || undefined}
                          value={it[k]}
                          onChange={(e) => setItem(idx, { [k]: e.target.value })}
                        />
                      </Field>
                    ))}
                  </div>
                  <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-border pt-3">
                    <Button
                      variant="ghost"
                      size="sm"
                      data-testid={`quotation-price-history-${idx}`}
                      onClick={() => loadHistory(idx)}
                    >
                      Previous price
                    </Button>
                    <span className="font-mono-num text-sm">
                      Line total{" "}
                      {inr2(
                        round2(
                          Number(it.quantity) * Number(it.rate) *
                            (1 - Number(it.discount_percent || 0) / 100) *
                            (1 + Number(it.gst_rate || 0) / 100)
                        )
                      )}
                    </span>
                  </div>
                </div>
              ))}
            </div>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              multiple
              hidden
              onChange={(e) => onUpload(Array.from(e.target.files || []))}
            />
          </div>

          <div className="surface p-5">
            <div className="overline mb-3">Charges, terms & template</div>
            <div className="grid gap-3 sm:grid-cols-4">
              {[
                ["installation_charge", "Installation ₹", "quotation-installation-input"],
                ["transportation_charge", "Transportation ₹", "quotation-transport-input"],
                ["other_charges", "Other charges ₹", null],
                ["charges_gst_rate", "Charges GST %", null],
              ].map(([k, label, tid]) => (
                <Field key={k} label={label}>
                  <Input
                    data-testid={tid || undefined}
                    value={form[k]}
                    onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                  />
                </Field>
              ))}
              <Field label="Template">
                <Select value={form.template} onValueChange={(v) => setForm({ ...form, template: v })}>
                  <SelectTrigger data-testid="quotation-template-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="premium">Premium Project Proposal</SelectItem>
                    <SelectItem value="corporate">Corporate (minimal)</SelectItem>
                    <SelectItem value="catalogue">Product Catalogue</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Photo layout">
                <Select
                  value={form.photo_layout}
                  onValueChange={(v) => setForm({ ...form, photo_layout: v })}
                >
                  <SelectTrigger data-testid="quotation-layout-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="above">Photos above product</SelectItem>
                    <SelectItem value="beside">Photos beside product</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Payment terms" className="sm:col-span-2">
                <Input
                  value={form.payment_terms}
                  onChange={(e) => setForm({ ...form, payment_terms: e.target.value })}
                />
              </Field>
              <Field label="Warranty" className="sm:col-span-2">
                <Input
                  value={form.warranty}
                  onChange={(e) => setForm({ ...form, warranty: e.target.value })}
                />
              </Field>
              <Field label="Terms & conditions" className="sm:col-span-4">
                <Textarea
                  rows={4}
                  data-testid="quotation-terms-input"
                  value={form.terms}
                  onChange={(e) => setForm({ ...form, terms: e.target.value })}
                />
              </Field>
            </div>
          </div>
        </div>

        <div className="space-y-4 lg:sticky lg:top-20 lg:self-start">
          <div className="surface p-5" data-testid="quotation-totals">
            <div className="overline mb-3">Live totals</div>
            {[
              ["Subtotal", totals.subtotal],
              ["Discount", -totals.discount],
              ["Charges", totals.charges],
              ["Taxable value", totals.taxable],
              ...(effectiveMode === "inter"
                ? [["IGST", totals.tax]]
                : [["CGST", round2(totals.tax / 2)], ["SGST", round2(totals.tax / 2)]]),
              ["Round off", round2(Math.round(totals.grand) - totals.grand)],
            ].map(([k, v]) => (
              <div key={k} className="flex justify-between border-b border-border py-2 text-sm">
                <span className="text-muted-foreground">{k}</span>
                <span className="font-mono-num">{inr2(v)}</span>
              </div>
            ))}
            <div className="mt-3 flex items-center justify-between rounded bg-primary px-3 py-2.5 text-primary-foreground">
              <span className="text-xs uppercase tracking-wider">Grand total</span>
              <span className="font-mono-num text-lg font-semibold">{inr2(Math.round(totals.grand))}</span>
            </div>
            <div className="mt-4 space-y-1 border-t border-border pt-3 text-xs">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Cost</span>
                <span className="font-mono-num">{inr2(totals.cost)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Margin</span>
                <span className="font-mono-num">
                  {inr2(totals.margin)} ({totals.marginPct}%)
                </span>
              </div>
            </div>
            {marginWarning && (
              <div
                data-testid="quotation-margin-warning"
                className="mt-3 flex gap-2 rounded border border-accent/50 bg-accent/10 p-2.5 text-xs text-accent"
              >
                <AlertTriangle className="h-4 w-4 shrink-0" />
                Margin {totals.marginPct}% is below the configured minimum of {minMargin}%.
              </div>
            )}
          </div>

          {priceHistory.length > 0 && (
            <div className="surface p-5" data-testid="price-history-panel">
              <div className="overline mb-2">Previous price (private)</div>
              {priceHistory.slice(0, 5).map((h) => (
                <div key={`${h.quotation_number}-${h.date}-${h.rate}`} className="border-b border-border py-2 text-xs last:border-0">
                  <div className="flex justify-between">
                    <span>{h.quotation_number}</span>
                    <span className="font-mono-num">{inr2(h.rate)}</span>
                  </div>
                  <div className="text-muted-foreground">
                    Qty {h.quantity} • {String(h.date).slice(0, 10)} • {h.status}
                  </div>
                </div>
              ))}
              <Badge variant="outline" className="mt-2 text-[10px]">
                Visible to authorised roles only
              </Badge>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
